import { Link } from "react-router-dom";
import { ExternalLink, BookOpen, ArrowRight } from "lucide-react";
import { cn } from "../lib/utils";
import GapCaptureBox from "./GapCaptureBox";

// Shared verdict-evidence UI. The single-product checker (ResultCard) and the
// Routine checker both render THESE components from the same /api/check +
// /api/routine/check `verified_findings` / `ingredient_chips` payload — one
// source strip, one insufficient-data panel, never a copy.

const TIER_DOT = {
  HIGH: "bg-avoid-deep",
  MEDIUM: "bg-caution-deep",
  LOW: "bg-safe-deep",
  SAFE: "bg-safe-deep",
};

export function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// One verified interaction row: ingredient × medication, plain explanation,
// evidence level, clickable "Source: {name} →", and "Reviewed by {who} · {date}".
export function VerifiedFinding({ f }) {
  const dateStr = fmtDate(f.verified_at);
  const dot = TIER_DOT[f.risk_tier] || "bg-ink";
  return (
    <div
      data-testid={`verified-finding-${f.ingredient_id}`}
      className="rounded-2xl border border-surface-line bg-white p-4 sm:p-5 space-y-2.5"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn("inline-block w-2 h-2 rounded-full", dot)} />
        <span className="font-bold text-ink">{f.ingredient_name}</span>
        <span className="text-ink-mute text-[13px]">× {f.medication_name}</span>
        {f.severity_label && (
          <span className="ml-auto text-[10px] uppercase tracking-[0.14em] font-bold text-ink-mute bg-cream-100 border border-surface-line rounded-full px-2 py-0.5">
            {f.severity_label}
          </span>
        )}
      </div>

      {f.explanation && (
        <p data-testid="finding-explanation" className="text-[14px] text-ink-soft leading-relaxed">
          {f.explanation}
        </p>
      )}
      {f.recommendation && (
        <p className="text-[13px] text-ink leading-relaxed">
          <span className="font-semibold">What to do: </span>{f.recommendation}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1">
        {f.evidence_label && (
          <span data-testid="finding-evidence" className="text-[11px] uppercase tracking-[0.12em] font-bold text-ink-mute">
            {f.evidence_label}
          </span>
        )}
        {f.source_url ? (
          <a
            data-testid="finding-source"
            href={f.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-ink underline underline-offset-2 hover:opacity-80"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Source: {f.source_name || "View source"}
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : (f.textbook_title ? (
          <span data-testid="finding-source" className="inline-flex items-center gap-1 text-[12.5px] text-ink-soft">
            <BookOpen className="w-3.5 h-3.5" />
            Source: {f.textbook_title}{f.textbook_page ? `, ${f.textbook_page}` : ""}
          </span>
        ) : null)}
      </div>

      {f.spot_check?.name ? (
        <div data-testid="finding-reviewer" className="text-[11.5px] text-ink-mute">
          Researched and reviewed by {f.spot_check.name}{f.spot_check.role ? `, ${f.spot_check.role}` : ""}
          {f.spot_check.date ? ` · Reviewed ${fmtDate(f.spot_check.date)}` : ""}
        </div>
      ) : f.reviewed_by ? (
        <div data-testid="finding-reviewer" className="text-[11.5px] text-ink-mute">
          Reviewed by {f.reviewed_by}{dateStr ? ` · ${dateStr}` : ""}
        </div>
      ) : null}
    </div>
  );
}

// The dashed "not yet verified for this medication" note shown under a
// verdict when some actives are covered and some are not.
export function UncoveredActivesNote({ uncovered }) {
  if (!uncovered || uncovered.length === 0) return null;
  return (
    <div
      data-testid="uncovered-actives"
      className="rounded-2xl border border-dashed border-surface-line bg-white/60 p-4 text-[13px] text-ink-soft"
    >
      <span className="font-semibold text-ink">Not yet verified for this medication: </span>
      {uncovered.map((u) => u.ingredient_name).join(", ")}
      <span className="opacity-70"> — we don't publish a verdict for these until a source is on file.</span>
    </div>
  );
}

// NOT ENOUGH VERIFIED DATA panel: ingredients we read, the required actives,
// optional not-found controls, and the "email me when verified" capture box.
export function InsufficientActivesPanel({
  result,
  onPasteFallback,
  onTryAnotherProduct,
  showRoutineLink = true,
  messageOverride,
  className,
}) {
  const chips = result.ingredient_chips || [];
  const cats = result.active_categories || [];
  const notFound = result.product_not_found;
  const hasNotFoundCtas = notFound && (onPasteFallback || onTryAnotherProduct);

  return (
    <div
      data-testid="insufficient-data-panel"
      className={cn(
        "rounded-2xl p-5 sm:p-6 space-y-4",
        className || "mt-6 bg-white/75 backdrop-blur border border-white/50",
      )}
    >
      <p className="text-[14px] leading-relaxed text-ink-soft">
        {messageOverride
          || result.data_status_message
          || "We don't have verified data on this combination yet. Every verdict on BlendSafe links to its FDA or published source — we're adding them one row at a time."}
      </p>

      {chips.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-2">
            Ingredients we read
          </div>
          <div className="flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <span
                key={c.ingredient_id}
                data-testid={`ing-chip-${c.ingredient_id}`}
                data-missing={c.missing ? "1" : "0"}
                className={cn(
                  "px-3 py-1.5 rounded-full border text-[12px] font-medium",
                  c.missing
                    ? "bg-caution-bg/50 text-caution-deep border-caution-border border-dashed"
                    : c.is_active
                      ? "bg-cream-100 text-ink border-surface-line"
                      : "bg-cream-50 text-ink-mute border-surface-line",
                )}
                title={c.missing ? "Active with no verified source yet" : (c.is_active ? "Active" : "Not an active we gate on")}
              >
                {c.user_input_name || c.name}
                {c.missing && <span className="ml-1 text-[10px] uppercase tracking-wide opacity-80">· needs source</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {cats.length > 0 && (
        <div data-testid="active-categories" className="text-[12px] text-ink-mute leading-relaxed">
          <span className="font-semibold text-ink-soft">Actives we require a verified source for: </span>
          {cats.join(", ")}
        </div>
      )}

      {(hasNotFoundCtas || showRoutineLink) && (
        <div className="flex flex-wrap items-center gap-2.5 pt-1">
          {hasNotFoundCtas ? (
            <>
              {onPasteFallback && (
                <button
                  type="button"
                  onClick={() => onPasteFallback?.()}
                  data-testid="insufficient-data-paste-cta"
                  className="inline-flex items-center justify-center px-4 h-10 rounded-full bg-ink text-cream-50 hover:bg-ink-soft text-[13px] font-bold transition-colors"
                >
                  Paste ingredient list
                </button>
              )}
              {onTryAnotherProduct && (
                <button
                  type="button"
                  onClick={() => onTryAnotherProduct?.()}
                  data-testid="insufficient-data-try-other"
                  className="inline-flex items-center justify-center px-4 h-10 rounded-full border border-surface-line text-ink hover:bg-cream-100 text-[13px] font-semibold transition-colors"
                >
                  Try another product
                </button>
              )}
            </>
          ) : (
            showRoutineLink && (
              <Link
                to="/routine"
                data-testid="insufficient-check-routine"
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-surface-line text-ink hover:bg-cream-100 text-[13px] font-semibold transition-colors"
              >
                Check your full routine
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )
          )}
        </div>
      )}

      <GapCaptureBox result={result} />
    </div>
  );
}
